/**
 * In-app location stack so Back can navigate to an explicit path
 * (keeps platform nav highlight in sync) instead of relying only on history.go(-1).
 */

let stack = [];
let ignoreNext = false;
let restoreScrollOnNextTrack = false;
/** While true, location sync must not rewrite the stack (trap pop in flight). */
let suspendTracking = false;
/** If a later location is not this path, sync sends the player here. */
let enforcedReturnPath = null;

function fullPath(pathname, search = "", hash = "") {
  return `${pathname || "/"}${search || ""}${hash || ""}`;
}

/**
 * Gateway and in-session URLs are hops into a live arena, not places Back should stop.
 * @param {string} path
 */
function isSessionHop(path) {
  const base = String(path || "").split("?")[0];
  if (base.startsWith("/room/")) return true;
  if (/^\/arena\/[^/]+/.test(base)) return true;
  if (/^\/tournaments\/(?!new(?:\/|$))[^/]+/.test(base)) return true;
  if (/^\/competitions\/[^/]+/.test(base)) return true;
  return false;
}

/**
 * @param {string} pathname
 * @param {string} [search]
 * @param {string} [hash]
 * @param {"PUSH"|"REPLACE"|"POP"} [action]
 */
export function trackPlatformLocation(pathname, search, hash, action = "PUSH") {
  if (suspendTracking) return;
  const path = fullPath(pathname, search, hash);

  if (ignoreNext) {
    ignoreNext = false;
    if (stack.length === 0) stack.push(path);
    else stack[stack.length - 1] = path;
    return;
  }

  if (action === "REPLACE") {
    if (stack.length === 0) stack.push(path);
    else stack[stack.length - 1] = path;
    return;
  }

  if (action === "POP") {
    while (stack.length > 1 && stack[stack.length - 1] !== path) {
      stack.pop();
    }
    if (stack.length === 0 || stack[stack.length - 1] !== path) {
      stack.push(path);
    }
    return;
  }

  // PUSH (or unknown)
  if (stack[stack.length - 1] !== path) {
    stack.push(path);
  }
}

/**
 * Marks that the next navigation is programmatic history back (sync stack on POP).
 */
export function signalHistoryBack() {
  if (stack.length >= 2) {
    stack.pop();
    ignoreNext = true;
    return true;
  }
  return false;
}

/** @returns {boolean} */
export function canNavigateHistoryBack() {
  return stack.length >= 2;
}

/**
 * Pops the current entry and returns the previous path, or `fallbackPath`.
 * Marks the next track call to sync without re-pushing.
 *
 * @param {string} fallbackPath
 * @returns {string}
 */
export function consumeBackTarget(fallbackPath = "/") {
  if (stack.length >= 2) {
    stack.pop();
    ignoreNext = true;
    return stack[stack.length - 1] || fallbackPath;
  }
  ignoreNext = true;
  return fallbackPath;
}

/** @returns {string | null} */
export function peekPreviousPath() {
  if (stack.length < 2) return null;
  return stack[stack.length - 2];
}

/**
 * Ignore stack updates until {@link resumeNavigationTracking}.
 * Used while a same-URL system-back trap is popped, so that pop cannot erase the origin.
 */
export function suspendNavigationTracking() {
  suspendTracking = true;
}

export function resumeNavigationTracking() {
  suspendTracking = false;
}

/**
 * @param {string} path
 */
export function armReturnEnforcement(path) {
  enforcedReturnPath = path || null;
}

/** @returns {string | null} */
export function peekReturnEnforcement() {
  return enforcedReturnPath;
}

export function clearReturnEnforcement() {
  enforcedReturnPath = null;
}

/**
 * Drop the current page and remember to restore scroll on the destination.
 * The next tracked location replaces the new top instead of pushing again.
 * @returns {boolean} whether scroll should be restored for that track
 */
export function markBackNavigation() {
  if (stack.length >= 1) stack.pop();
  ignoreNext = true;
  restoreScrollOnNextTrack = true;
}

/** @returns {boolean} */
export function takeScrollRestoreFlag() {
  const pending = restoreScrollOnNextTrack;
  restoreScrollOnNextTrack = false;
  return pending;
}

/**
 * Leave a live session and return to the page that opened it.
 * In-room tabs and arena/tournament gateway hops are skipped.
 * @param {string} [fallbackPath]
 * @returns {string}
 */
export function consumeSessionExitTarget(fallbackPath = "/arena") {
  while (stack.length >= 1 && isSessionHop(stack[stack.length - 1])) {
    stack.pop();
  }
  ignoreNext = true;
  restoreScrollOnNextTrack = true;
  if (stack.length >= 1) return stack[stack.length - 1];
  return fallbackPath || "/arena";
}

/** Test helper */
export function resetNavigationMemory() {
  stack = [];
  ignoreNext = false;
  restoreScrollOnNextTrack = false;
  suspendTracking = false;
  enforcedReturnPath = null;
}

/** Test helper */
export function getNavigationStack() {
  return [...stack];
}
